import { useEffect, useState, useRef } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  selectAuthError,
  selectAuthQueryState,
  selectAuthTokenVerification,
} from 'store/auth/auth.selector';
import { groupAWS, getPermissions } from 'store/account/account.thunk';
import { verifyToken } from 'store/auth/auth.thunk';
import { TOGGLE_LOADER } from 'store/app/app.actions';
import { SET_ACCESS_TYPE, SET_AUTH_USERID, SET_AUTH_JWT } from 'store/auth/auth.actions';
import {
  SET_ACCOUNT,
  SET_DB_SETTING,
  SET_INC_SETTINGS,
  SET_GROUP,
} from 'store/account/account.actions';
import { QueryState, SitePermissionTypes } from '@/common/enum';
import { ErrorMessage, Loading } from '@/components/primitives';
import { GetSitePermission } from '@/utils';
import {
  accountSelector,
  palmettoGroupsSelector,
  hasFetchedDashboardSettingsSelector,
  accountIncidentSelector,
} from 'store/account/account.selector';
import {
  SET_USER_SELECTED_PROGRAMS,
  SET_PROGRAM_WS_UPDATES,
  SET_PROG_IN_FULLSCREEN,
  SET_NOTIFICATIONS,
} from 'store/app/app.actions';
import {
  programsPermissionsSelector,
  dashboardSettingsSelector,
} from 'store/account/account.selector';
import useWebSocket from 'hooks/useWebSocket';
import useNotification from 'hooks/useNotification';
import { WSRoomNames } from '@/common/enum';
import {
  progInFullScreenSelector,
  programsListWSUpdatesSelector,
  userProgramSelector,
  selectNotificationType,
} from 'store/app/app.selector';
import { useFetchDBSettingsQuery, useFetchIncSettingsQuery } from '@/services/settingsApi';
import { defaultDashboardSettings } from 'store/account/account.state';

const AuthPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idToken = searchParams.get('id_token');
  const urlPath = location.pathname;

  const palmettoGroups = useAppSelector(palmettoGroupsSelector);
  const selectedIncidents = useAppSelector(accountIncidentSelector);
  const authTokenVerification = useAppSelector(selectAuthTokenVerification);
  const authTokenError = useAppSelector(selectAuthError);
  const isLoading = useAppSelector(selectAuthQueryState) === QueryState.AWAIT;
  const isError = useAppSelector(selectAuthQueryState) === QueryState.FAIL;
  const isReady = useAppSelector(selectAuthQueryState) === QueryState.READY;
  const programsPermissions = useAppSelector(programsPermissionsSelector);
  const dashboardSettings = useAppSelector(dashboardSettingsSelector);
  const hasFetchedDashboardSettings = useAppSelector(hasFetchedDashboardSettingsSelector);
  const userProgramsListWSUpdates = useAppSelector(programsListWSUpdatesSelector);
  const notificationType = useAppSelector(selectNotificationType);
  const progInFullScreen = useAppSelector(progInFullScreenSelector);
  const notificationTypeRef = useRef(notificationType);
  const progInFullScreenRef = useRef(progInFullScreen);

  const account = useAppSelector(accountSelector) || undefined;
  const selectedPositions = account?.currentPositions;
  const programsList = useAppSelector(userProgramSelector);
  const selectedProgramIds = programsList
    ?.filter((program) => program.isActive && Number(program.id) !== 45)
    .map((program) => Number(program.id));

  const prevSelectedProgramIdsRef = useRef<number[]>([]);

  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState('Something went wrong, please try again later.');

  // *: RTK Query Hooks
  const {
    data: dbSettingsResp,
    isLoading: loadingDBSettings,
    isFetching: fetchingDBSettings,
    error: dbSettingsError,
  } = useFetchDBSettingsQuery(authTokenVerification, {
    skip: !authTokenVerification,
    refetchOnMountOrArgChange: true,
  });
  const {
    data: incSettingsResp,
    error: incSettingError,
    refetch: refetchIncSettings,
  } = useFetchIncSettingsQuery(authTokenVerification, {
    skip: !authTokenVerification,
    refetchOnMountOrArgChange: true,
  });

  const { connect, joinRoom, onConnect } = useWebSocket();
  const { currentNotifications } = useNotification({
    groupId: account?.pvActiveGroupID,
    selectedIncidents,
    joinRoom,
    onConnect,
  });

  const isDisplayView = urlPath.startsWith('/display-view/');
  const isPrintView = urlPath.startsWith('/print-view/');

  useEffect(() => {
    if (selectedPositions?.length === 0) return;
    dispatch(getPermissions());
  }, [selectedPositions]);

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      return;
    }
    if (dbSettingsResp) {
      const settings =
        Array.isArray(dbSettingsResp) && dbSettingsResp.length
          ? dbSettingsResp[0]
          : defaultDashboardSettings;
      dispatch(SET_DB_SETTING(settings));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbSettingsResp, dispatch]);

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      return;
    }
    if (incSettingsResp) {
      const settings =
        Array.isArray(incSettingsResp) && incSettingsResp.length ? incSettingsResp[0] : null;
      dispatch(SET_INC_SETTINGS(settings));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incSettingsResp, dispatch]);

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      return;
    }
    dispatch(SET_NOTIFICATIONS(currentNotifications));
  });

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      return;
    }
    notificationTypeRef.current = notificationType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationType]);

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      return;
    }
    progInFullScreenRef.current = progInFullScreen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progInFullScreen]);

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      dispatch(SET_AUTH_JWT(idToken));
      return;
    }
    if (idToken) {
      // * Dispatch Palmetto Group
      dispatch(groupAWS());
      // * Dispatch Auth Token verification
      dispatch(verifyToken(idToken));
      dispatch(TOGGLE_LOADER(true));
      dispatch(SET_AUTH_JWT(idToken));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken]);

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      return;
    }
    if (!account || !selectedIncidents?.length || !selectedProgramIds?.length) return;

    const prevSelected = prevSelectedProgramIdsRef.current || [];

    const addedIds = selectedProgramIds.filter((id) => !prevSelected.includes(id));

    // === UN-JOIN WS ROOMS ===
    // DISABLED FOR NOW (deconstruct leaveRoom from useWebSocket)
    // const removedIds = prevSelected.filter((id) => !selectedProgramIds.includes(id));
    // removedIds.forEach((id) => {
    //   const matchingRooms = WSRoomNames.filter((room) => room.programId === id);
    //
    //   matchingRooms.forEach((room) => {
    //     // Check if the room is still needed by any of the currently selected program IDs
    //     const isRoomStillNeeded = WSRoomNames.some(
    //       (r) => r.roomName === room.roomName && selectedProgramIds.includes(r.programId)
    //     );
    //
    //     if (!isRoomStillNeeded) {
    //       leaveRoom(room.roomName);
    //     }
    //   });
    // });

    prevSelectedProgramIdsRef.current = selectedProgramIds;

    type ConnectWsParams = {
      userId: number;
      groupId: number;
      isSuperAdmin: boolean;
      accessToken: string;
    };

    const connectWs = async ({ userId, groupId, isSuperAdmin, accessToken }: ConnectWsParams) => {
      await connect(userId, groupId, isSuperAdmin, accessToken);
    };

    const callbackWS = async (
      data: { pvIncidentID?: string; cbrnMIMEContentCodeSimpleType: string },
      roomName: string
    ) => {
      let programId: number;
      if (roomName === 'dockets') {
        if (data.cbrnMIMEContentCodeSimpleType) {
          programId = data.cbrnMIMEContentCodeSimpleType.includes('bookmark') ? 13 : 15;
        }
      } else {
        programId = WSRoomNames.find((room) => room.roomName === roomName)?.programId;
      }

      if (programId) {
        if (
          (data.pvIncidentID && !selectedIncidents.includes(data.pvIncidentID)) ||
          userProgramsListWSUpdates.includes(programId)
        ) {
          return;
        }
        dispatch(SET_PROGRAM_WS_UPDATES(programId));
      }
    };

    connectWs({
      userId: account.id,
      groupId: account.pvActiveGroupID,
      isSuperAdmin: false,
      accessToken: account.accessToken || '',
    }).then(() => {
      WSRoomNames.filter((room) => addedIds.includes(room.programId)).forEach((room) =>
        joinRoom(room.roomName, callbackWS)
      );
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, selectedIncidents, selectedProgramIds]);

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      return;
    }
    if (
      isReady &&
      authTokenVerification &&
      authTokenVerification.attributes &&
      authTokenVerification.accessToken
    ) {
      let accessType: SitePermissionTypes;
      const groupId = authTokenVerification.pvActiveGroupID;
      if (!groupId || groupId === 0) {
        setHasError(true);
        setErrorMsg('No Active Group Found.');
        dispatch(TOGGLE_LOADER(false));
        return;
      }
      const activeGroup =
        palmettoGroups && palmettoGroups.find((group) => group.pvGroupID === groupId);
      const sitePermission = GetSitePermission('BOARD', groupId, authTokenVerification.attributes);
      const permissions = sitePermission.sitePermissions;
      // *: Permission Type
      switch (permissions && permissions[0]) {
        case 'A':
          accessType = SitePermissionTypes.A;
          break;
        case 'U':
          accessType = SitePermissionTypes.U;
          break;
        case 'G':
          accessType = SitePermissionTypes.G;
          break;
        default:
          accessType = SitePermissionTypes.A; // !TODO: Change to null if done testing
      }
      if (!accessType) {
        setHasError(true);
        setErrorMsg("You don't have access to the Palmetto Dashboard App.");
        return;
      }

      const userAuthCreds = {
        id: authTokenVerification.accessToken,
        userId: authTokenVerification.id,
        accessType: accessType,
      };
      sessionStorage.setItem('userAuthCreds', JSON.stringify(userAuthCreds));
      const userID = { userId: authTokenVerification.id };
      dispatch(SET_AUTH_USERID(userID));
      dispatch(SET_ACCESS_TYPE(accessType));
      dispatch(SET_ACCOUNT(authTokenVerification));
      // dispatch(getPermissions());
      if (activeGroup) {
        dispatch(
          SET_GROUP({ pvGroupID: activeGroup.pvGroupID, pvGroupName: activeGroup.pvGroupName })
        );
      }
      dispatch(TOGGLE_LOADER(false));
      if (urlPath.includes('display-view')) {
        return navigate(urlPath);
      }
      if (urlPath.includes('settings')) {
        return navigate(urlPath);
      }
      if (urlPath.includes('reports-view')) {
        console.log('🚀 urlPath: ', urlPath);
        return navigate(urlPath);
      }
      return navigate('/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTokenVerification, dispatch, isReady, navigate, palmettoGroups, urlPath]);

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      return;
    }
    const refetchIncidentSettings = () => {
      refetchIncSettings();
    };
    window.addEventListener('selected-incidents-updated', refetchIncidentSettings);
    return () => {
      window.removeEventListener('selected-incidents-updated', refetchIncidentSettings);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      return;
    }
    if (!programsPermissions?.length || !hasFetchedDashboardSettings) return;
    const settings = dashboardSettings?.pvSettingValue
      ? JSON.parse(dashboardSettings.pvSettingValue)
      : null;
    const programInFullScreen = settings?.onBoarding?.programInFullScreen;

    if (programInFullScreen) {
      dispatch(SET_PROG_IN_FULLSCREEN(programInFullScreen));
    }

    dispatch(
      SET_USER_SELECTED_PROGRAMS({
        programsPermissions: programsPermissions,
        dashboardSettings: dashboardSettings || null,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programsPermissions, hasFetchedDashboardSettings, dashboardSettings, dispatch]);

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      return;
    }
    if (authTokenError) {
      setHasError(true);
      return setErrorMsg(authTokenError);
    }
    if (isError && !authTokenError) {
      setHasError(true);
      return setErrorMsg('Something went wrong, please try again later.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTokenError, isError]);

  useEffect(() => {
    if (isDisplayView || isPrintView) {
      return;
    }
    if (isReady && urlPath === '/' && !authTokenVerification) return navigate('/login');
    if ((!isReady || !isLoading) && urlPath === '/' && !authTokenVerification)
      return navigate('/login');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, urlPath, isReady, isLoading, authTokenVerification]);

  if (isLoading || loadingDBSettings || fetchingDBSettings) return <Loading />;

  if (hasError || !!dbSettingsError || !!incSettingError)
    return <ErrorMessage message={errorMsg} />;

  return <Outlet />;
};

export default AuthPage;
